import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { EmptyState } from '@/components/patterns/empty-state'
import { AppUpdateThumbnail } from '@/components/app-updates/AppUpdateThumbnail'
import { getIndexPaginationMeta, IndexTablePagination } from '@/components/tables/IndexTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { useAbility } from '@/lib/casl'
import { cn } from '@/lib/utils'
import dayjs from '@/lib/dayjs'
import {
  appUpdatesQueries,
  useMarkAppUpdatesViewedMutation,
  useAppUpdates,
} from '@/lib/services/app-updates/app-updates.query'
import {
  APP_UPDATE_FEATURED_COUNT,
  APP_UPDATE_PAGE_SIZE,
  appUpdateCategoryOptions,
  appUpdateSortOptions,
  appUpdatesSearchSchema,
} from '@/lib/services/app-updates/app-updates.schema'
import {
  canCreateAppUpdates,
  canViewUnpublishedAppUpdates,
  isGlobalOrganizationContext,
} from '@/lib/services/app-updates/app-updates.utils'
import { createFileRoute, Link, stripSearchParams, useNavigate } from '@tanstack/react-router'
import { ArrowRight, CalendarDays, Search, Sparkles } from 'lucide-react'
import { useEffect, useEffectEvent, useRef } from 'react'

const defaultSearch = {
  page: 1,
  pageSize: APP_UPDATE_PAGE_SIZE,
  search: '',
  category: 'all',
  sort: 'recent',
}

export const Route = createFileRoute('/_authenticated/$orgId/whats-new/')({
  component: WhatsNewIndexPage,
  validateSearch: (search) => appUpdatesSearchSchema.parse(search ?? {}),
  search: {
    middlewares: [stripSearchParams(defaultSearch)],
  },
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ context, deps: { search }, params }) => {
    const routeSearch = {
      ...defaultSearch,
      ...(search ?? {}),
    }
    const filters = getAppUpdateFilters(routeSearch)

    await Promise.all([
      context.queryClient.ensureQueryData(
        appUpdatesQueries.index({
          organizationId: params.orgId,
          filters,
          page: 1,
          pageSize: APP_UPDATE_FEATURED_COUNT,
        })
      ),
      context.queryClient.ensureQueryData(
        appUpdatesQueries.index({
          organizationId: params.orgId,
          filters,
          offset: APP_UPDATE_FEATURED_COUNT,
          page: routeSearch.page,
          pageSize: routeSearch.pageSize,
        })
      ),
    ])

    return { isReadOnly: !canViewUnpublishedAppUpdates(context.ability) }
  },
})

function formatUpdateDate(value) {
  if (!value) {
    return 'Today'
  }

  return dayjs.utc(value).format('MMM D, YYYY')
}

const updateSummaryPreviewLength = 150

function getAppUpdateSummary(appUpdate) {
  const text = String(appUpdate?.description || '').trim()

  if (!text) {
    return null
  }

  return text.length > updateSummaryPreviewLength
    ? text.slice(0, updateSummaryPreviewLength).trimEnd() + '...'
    : text
}

function getAppUpdateFilters(search) {
  return {
    ...(search.search ? { search: search.search } : {}),
    ...(search.category !== 'all' ? { category: search.category } : {}),
    sort: search.sort,
  }
}

function AppUpdateMeta({ appUpdate, showAudience, showPublishingState }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <Badge variant="secondary">{appUpdate.update_type_label}</Badge>
      {showAudience ? (
        <Badge variant={appUpdate.audience === 'global' ? 'warning' : 'success'}>
          {appUpdate.audience_label}
        </Badge>
      ) : null}
      <Badge variant={appUpdate.is_read ? 'muted' : 'brand'}>
        {appUpdate.is_read ? 'Read' : 'Unread'}
      </Badge>
      {showPublishingState && !appUpdate.is_published ? (
        <Badge variant="warning">Unpublished</Badge>
      ) : null}
      <span className="inline-flex items-center gap-1.5">
        <CalendarDays className="size-4" />
        {formatUpdateDate(appUpdate.update_date)}
      </span>
    </div>
  )
}

function ReadMoreCue({ className }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-sm font-medium text-interaction transition-colors group-hover:text-action-pressed dark:group-hover:text-action',
        className
      )}
    >
      Read more
      <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
    </span>
  )
}

function FeaturedAppUpdateCard({ orgId, appUpdate, showAudience, showPublishingState }) {
  const summary = getAppUpdateSummary(appUpdate)

  return (
    <Link
      to="/$orgId/whats-new/$appUpdateId"
      params={{ orgId, appUpdateId: appUpdate.id }}
      className="group block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card interactive className="h-full overflow-hidden">
        <CardContent className="h-full p-0">
          <div className="grid h-full min-h-44 gap-0 sm:h-48 sm:grid-cols-[minmax(0,1fr)_42%]">
            <div className="grid min-h-0 grid-rows-[auto_auto_minmax(0,1fr)_auto] gap-2 overflow-hidden p-4">
              <AppUpdateMeta
                appUpdate={appUpdate}
                showAudience={showAudience}
                showPublishingState={showPublishingState}
              />
              <Typography variant="h4" className="line-clamp-2 min-w-0 break-words pb-0">
                {appUpdate.title}
              </Typography>
              {summary ? (
                <div className="min-h-0 overflow-hidden">
                  <Typography variant="supporting" className="line-clamp-2 block min-w-0">
                    {summary}
                  </Typography>
                </div>
              ) : null}
              <ReadMoreCue />
            </div>
            <div className="flex min-h-36 items-center justify-center p-4 sm:h-full">
              <div className="aspect-video w-full overflow-hidden rounded-lg bg-muted/30">
                <AppUpdateThumbnail
                  appUpdate={appUpdate}
                  className="h-full w-full rounded-none object-cover"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

function RefinementCard({ orgId, appUpdate, showAudience, showPublishingState }) {
  const summary = getAppUpdateSummary(appUpdate)

  return (
    <Link
      to="/$orgId/whats-new/$appUpdateId"
      params={{ orgId, appUpdateId: appUpdate.id }}
      className="group block h-full rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Card interactive className="h-full overflow-hidden">
        <CardContent className="flex h-full flex-col p-0">
          <div className="p-4 pb-0">
            <AppUpdateThumbnail appUpdate={appUpdate} className="rounded-md" />
          </div>
          <div className="flex flex-1 flex-col gap-3 p-4">
            <AppUpdateMeta
              appUpdate={appUpdate}
              showAudience={showAudience}
              showPublishingState={showPublishingState}
            />
            <div className="space-y-2">
              <Typography variant="h5" className="line-clamp-2 break-words pb-0">
                {appUpdate.title}
              </Typography>
              {summary ? (
                <Typography variant="supporting" className="line-clamp-3 block">
                  {summary}
                </Typography>
              ) : null}
            </div>
            <ReadMoreCue className="mt-auto" />
          </div>
        </CardContent>
      </Card>
    </Link>
  )
}

function AppUpdateFilters({ search, updateSearch }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border bg-card p-3">
      <div className="relative w-full lg:max-w-[50%] lg:flex-[0_1_50%]">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search.search}
          onChange={(event) => updateSearch({ page: 1, search: event.target.value })}
          placeholder="Search app updates"
          className="pl-9"
        />
      </div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Sort by</span>
          <Select value={search.sort} onValueChange={(sort) => updateSearch({ page: 1, sort })}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {appUpdateSortOptions.map((option) => (
                <SelectItem key={option.id} value={option.id}>
                  {option.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {appUpdateCategoryOptions.map((option) => (
            <Button
              key={option.id}
              type="button"
              variant={search.category === option.id ? 'selection' : 'outline'}
              size="sm"
              onClick={() => updateSearch({ page: 1, category: option.id })}
            >
              {option.name}
            </Button>
          ))}
        </div>
      </div>
    </div>
  )
}

export function WhatsNewEmptyState({ canCreate = false }) {
  return (
    <EmptyState
      icon={Sparkles}
      title={canCreate ? 'No app updates yet' : 'Coming soon'}
      description={
        canCreate
          ? 'Create the first app update when the next release note is ready.'
          : 'New features, fixes, and improvements will show up here as soon as they are ready.'
      }
    />
  )
}

export function hasLoadedAppUpdates({
  featuredAppUpdatesResponse = null,
  recentAppUpdatesResponse = null,
  featuredAppUpdates = [],
  recentAppUpdates = [],
} = {}) {
  const featuredCount = Array.isArray(featuredAppUpdatesResponse?.data)
    ? featuredAppUpdatesResponse.data.length
    : featuredAppUpdates.length
  const recentTotal = Number(recentAppUpdatesResponse?.meta?.total ?? recentAppUpdates.length)

  return featuredCount > 0 || (Number.isFinite(recentTotal) ? recentTotal > 0 : false)
}

export function WhatsNewIndexPage() {
  const routeSearch = Route.useSearch()
  const search = {
    ...defaultSearch,
    ...(routeSearch ?? {}),
  }
  const { orgId } = Route.useParams()
  const navigate = useNavigate({ from: Route.fullPath })
  const ability = useAbility()
  const { organization } = useOrganizationContext()
  const isGlobalContext = isGlobalOrganizationContext(organization)
  const canCreate = isGlobalContext && canCreateAppUpdates(ability)
  const canViewPublishingState = isGlobalContext && canViewUnpublishedAppUpdates(ability)
  const canViewAudienceTag = ability.can('access', 'global') && isGlobalContext
  const filters = getAppUpdateFilters(search)
  const markViewedMutation = useMarkAppUpdatesViewedMutation()
  const markedViewedOrgRef = useRef(null)
  const markAppUpdatesViewed = useEffectEvent(() => {
    markViewedMutation.mutate()
  })
  const { data: featuredAppUpdatesResponse } = useAppUpdates({
    filters,
    page: 1,
    pageSize: APP_UPDATE_FEATURED_COUNT,
  })
  const { data: recentAppUpdatesResponse } = useAppUpdates({
    filters,
    offset: APP_UPDATE_FEATURED_COUNT,
    page: search.page,
    pageSize: search.pageSize,
  })
  const featuredAppUpdates = search.page === 1 ? (featuredAppUpdatesResponse?.data ?? []) : []
  const recentAppUpdates = recentAppUpdatesResponse?.data ?? []
  const hasAppUpdates = hasLoadedAppUpdates({
    featuredAppUpdatesResponse,
    recentAppUpdatesResponse,
    featuredAppUpdates,
    recentAppUpdates,
  })
  const paginationMeta = getIndexPaginationMeta(
    recentAppUpdatesResponse,
    search.page,
    search.pageSize
  )
  useEffect(() => {
    if (!orgId || markedViewedOrgRef.current === String(orgId)) {
      return
    }

    markedViewedOrgRef.current = String(orgId)
    markAppUpdatesViewed()
  }, [orgId])

  const updateSearch = (nextSearch) => {
    void navigate({
      search: (current) => ({
        ...defaultSearch,
        ...current,
        ...nextSearch,
      }),
      // Matches the other index tables: list state replaces history, so Back leaves the list
      // instead of replaying filters or returning to a page the pagination just stepped off.
      replace: true,
    })
  }

  return (
    <div className="w-full max-w-none space-y-6">
      <AdminPageHeader
        icon={Sparkles}
        title="What's New"
        description="Stay updated with the latest features, fixes, and improvements."
        actions={
          canCreate ? (
            <CreateActionButton asChild>
              <Link to="/$orgId/whats-new/create" params={{ orgId }}>
                New App Update
              </Link>
            </CreateActionButton>
          ) : null
        }
      />

      <AppUpdateFilters search={search} updateSearch={updateSearch} />

      {featuredAppUpdates.length > 0 ? (
        <div className="grid gap-4 lg:grid-cols-2">
          {featuredAppUpdates.map((appUpdate) => (
            <FeaturedAppUpdateCard
              key={appUpdate.id}
              orgId={orgId}
              appUpdate={appUpdate}
              showAudience={canViewAudienceTag}
              showPublishingState={canViewPublishingState}
            />
          ))}
        </div>
      ) : null}

      {recentAppUpdates.length > 0 ? (
        <section className="space-y-4">
          <div>
            <Typography variant="h3" className="pb-1">
              Recent Refinements
            </Typography>
            <Typography variant="supporting" className="block">
              Other updates, fixes, and quality improvements.
            </Typography>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {recentAppUpdates.map((appUpdate) => (
              <RefinementCard
                key={appUpdate.id}
                orgId={orgId}
                appUpdate={appUpdate}
                showAudience={canViewAudienceTag}
                showPublishingState={canViewPublishingState}
              />
            ))}
          </div>
        </section>
      ) : null}

      {!hasAppUpdates ? <WhatsNewEmptyState canCreate={canCreate} /> : null}

      {paginationMeta.total > 0 || search.page > 1 ? (
        <IndexTablePagination
          meta={paginationMeta}
          pageSize={search.pageSize}
          onPageChange={(page) => updateSearch({ page })}
          onPageSizeChange={(pageSize) => updateSearch({ page: 1, pageSize })}
          pageSizeLabel="Items per page"
          pageSizeOptions={[9, 18, 27]}
        />
      ) : null}
    </div>
  )
}
